import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    IconButton,
    Box,
    Typography,
    Divider,
    Collapse,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Menu as MenuIcon,
    ChevronLeft as ChevronLeftIcon,
    Dashboard as DashboardIcon,
    People as PeopleIcon,
    Groups as GroupsIcon,
    Schedule as ScheduleIcon,
    CheckCircle as AttendanceIcon,
    Assessment as ReportsIcon,
    Warning as InterventionIcon,
    ExpandLess,
    ExpandMore,
    Home as HomeIcon,
    Person as StudentIcon,
    School as TeacherIcon
} from '@mui/icons-material';

const DRAWER_WIDTH = 280;

const Sidebar = ({ userType }) => {
    const [open, setOpen] = useState(true);
    const [expandedSections, setExpandedSections] = useState({});
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleDrawerToggle = () => {
        setOpen(!open);
    };

    const handleSectionToggle = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleNavigation = (path) => {
        navigate(path);
        if (isMobile) {
            setOpen(false);
        }
    };

    const isActive = (path) => {
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const menuItems = [
        {
            title: 'Dashboard',
            icon: <DashboardIcon />,
            path: userType === 'teacher' ? '/teacher-dashboard' : '/student-dashboard',
            single: true
        },
        {
            title: 'Students',
            icon: <PeopleIcon />,
            expandable: true,
            section: 'students',
            items: [
                { title: 'Student Directory', path: '/students/list', icon: <StudentIcon /> },
                { title: 'Manage Students', path: '/manage-students', icon: <PeopleIcon /> },
                { title: 'Add Student', path: '/students/add', icon: <PeopleIcon /> },
                { title: 'Upload Students', path: '/students/upload-students', icon: <PeopleIcon /> }
            ]
        },
        {
            title: 'Groups',
            icon: <GroupsIcon />,
            expandable: true,
            section: 'groups',
            items: [
                { title: 'Groups Home', path: '/groups', icon: <GroupsIcon /> },
                { title: 'Manage Core Groups', path: '/groups/manage', icon: <GroupsIcon /> },
                { title: 'Manage CTE Groups', path: '/groups/manage-cte', icon: <GroupsIcon /> },
                { title: 'Homeroom Groups', path: '/groups/homeroom', icon: <HomeIcon /> },
                { title: 'Add Group', path: '/groups/add', icon: <GroupsIcon /> }
            ]
        },
        {
            title: 'Schedules',
            icon: <ScheduleIcon />,
            expandable: true,
            section: 'schedules',
            items: [
                { title: 'Schedule Home', path: '/schedules', icon: <ScheduleIcon /> },
                { title: 'Section Periods', path: '/schedules/create-section', icon: <ScheduleIcon /> },
                { title: 'Block Schedules', path: '/schedules/block-schedules', icon: <ScheduleIcon /> },
                { title: 'Schedule Tool', path: '/schedules/tool', icon: <ScheduleIcon /> }
            ]
        },
        {
            title: 'Attendance',
            icon: <AttendanceIcon />,
            expandable: true,
            section: 'attendance',
            items: [
                { title: 'Take Attendance', path: '/attendance', icon: <AttendanceIcon /> }
            ]
        },
        {
            title: 'Interventions',
            icon: <InterventionIcon />,
            expandable: true,
            section: 'interventions',
            items: [
                { title: 'Intervention Dashboard', path: '/interventions', icon: <InterventionIcon /> },
                { title: 'Student Risk Profiles', path: '/interventions/students', icon: <StudentIcon /> },
                { title: 'Active Cases', path: '/interventions/cases', icon: <InterventionIcon /> }
            ]
        },
        {
            title: 'Reports',
            icon: <ReportsIcon />,
            expandable: true,
            section: 'reports',
            items: [
                { title: 'Attendance Reports', path: '/reports', icon: <ReportsIcon /> },
                { title: 'Advanced Reports', path: '/reports/v2', icon: <ReportsIcon /> },
                { title: 'Group Reports', path: '/reports/groups', icon: <GroupsIcon /> }
            ]
        }
    ];

    const drawer = (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ 
                p: 2, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                borderBottom: 1,
                borderColor: 'divider'
            }}>
                {open && (
                    <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
                        Group Track
                    </Typography>
                )}
                <IconButton onClick={handleDrawerToggle}>
                    {open ? <ChevronLeftIcon /> : <MenuIcon />}
                </IconButton>
            </Box>

            {/* User Type Badge */}
            {open && (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Box sx={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        gap: 1, 
                        px: 2, 
                        py: 1, 
                        borderRadius: 2, 
                        bgcolor: userType === 'teacher' ? 'primary.light' : 'secondary.light',
                        color: 'white'
                    }}>
                        {userType === 'teacher' ? <TeacherIcon /> : <StudentIcon />}
                        <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                            {userType}
                        </Typography>
                    </Box>
                </Box>
            )}

            <Divider />

            {/* Navigation Items */}
            <List sx={{ flexGrow: 1, py: 1 }}>
                {menuItems.map((item) => (
                    <React.Fragment key={item.title}>
                        {item.single ? (
                            <ListItem disablePadding>
                                <ListItemButton
                                    onClick={() => handleNavigation(item.path)}
                                    selected={isActive(item.path)}
                                    sx={{
                                        minHeight: 48,
                                        justifyContent: open ? 'initial' : 'center',
                                        px: 2.5,
                                    }}
                                >
                                    <ListItemIcon sx={{
                                        minWidth: 0,
                                        mr: open ? 3 : 'auto',
                                        justifyContent: 'center',
                                    }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={item.title} 
                                        sx={{ opacity: open ? 1 : 0 }} 
                                    />
                                </ListItemButton>
                            </ListItem>
                        ) : (
                            <>
                                <ListItem disablePadding>
                                    <ListItemButton
                                        onClick={() => handleSectionToggle(item.section)}
                                        sx={{
                                            minHeight: 48,
                                            justifyContent: open ? 'initial' : 'center',
                                            px: 2.5,
                                        }}
                                    >
                                        <ListItemIcon sx={{
                                            minWidth: 0,
                                            mr: open ? 3 : 'auto',
                                            justifyContent: 'center',
                                        }}>
                                            {item.icon}
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary={item.title} 
                                            sx={{ opacity: open ? 1 : 0 }} 
                                        />
                                        {open && (expandedSections[item.section] ? <ExpandLess /> : <ExpandMore />)}
                                    </ListItemButton>
                                </ListItem>
                                <Collapse in={expandedSections[item.section] && open} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {item.items.map((subItem) => (
                                            <ListItem key={subItem.title} disablePadding>
                                                <ListItemButton
                                                    onClick={() => handleNavigation(subItem.path)}
                                                    selected={isActive(subItem.path)}
                                                    sx={{ pl: 4 }}
                                                >
                                                    <ListItemIcon sx={{ minWidth: 40 }}>
                                                        {subItem.icon}
                                                    </ListItemIcon>
                                                    <ListItemText primary={subItem.title} />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </List>
                                </Collapse>
                            </>
                        )}
                    </React.Fragment>
                ))}
            </List>
        </Box>
    );

    return (
        <Drawer
            variant={isMobile ? 'temporary' : 'permanent'}
            open={isMobile ? open : true}
            onClose={isMobile ? handleDrawerToggle : undefined}
            sx={{
                width: open ? DRAWER_WIDTH : theme.spacing(7),
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: open ? DRAWER_WIDTH : theme.spacing(7),
                    boxSizing: 'border-box',
                    transition: theme.transitions.create('width', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    overflowX: 'hidden',
                },
            }}
        >
            {drawer}
        </Drawer>
    );
};

export default Sidebar;
